import React, { Dispatch, RefAttributes, memo } from "react";
import { Row, react, useRefWithSetter } from "uikit";
import { FieldErrors } from "react-hook-form";

import useObjectEditor from "../../../../../../../../../hooks/useObjectEditor";
import PaymentSystem, {
	PaymentProvider,
} from "../../../../../../../../../services/PaymentSystem";
import {
	StyledRow,
	StyledColumn,
} from "../../../../../../../../../components/common";
import {
	AssetPayment,
	Companies,
	MaibPayment,
	DinarPayment,
	BogPayment,
	CybersourcePayment,
	PlatonPayment,
	MonobankPayment,
	Name,
	RefundPolicy,
	Root,
	SelectPaymentSystems,
	SystemFee,
	Withdraw,
} from "../../../../../components";

import InternalController from "./Controller";

const ContentBase = react.withController<Content.PropsBase, Content.Controller>(
	({ value, onChange, controller }) => {
		const [nameRef, setNameRef] = useRefWithSetter<Name.Ref | null>(null);
		const [refundPolicyRef, setRefundPolicyRef] =
			useRefWithSetter<RefundPolicy.Ref | null>(null);
		const [companyBranchRef, setCompanyBranchRef] =
			useRefWithSetter<Companies.Ref | null>(null);
		const [assetPaymentRef, setAssetPaymentRef] =
			useRefWithSetter<AssetPayment.Ref | null>(null);
		const [maibPaymentRef, setMaibPaymentRef] =
			useRefWithSetter<MaibPayment.Ref | null>(null);
		const [dinarPaymentRef, setDinarPaymentRef] =
			useRefWithSetter<DinarPayment.Ref | null>(null);
		const [bogPaymentRef, setBogPaymentRef] =
			useRefWithSetter<BogPayment.Ref | null>(null);
		const [cybersourcePaymentRef, setCybersourcePaymentRef] =
			useRefWithSetter<CybersourcePayment.Ref | null>(null);
		const [platonPaymentRef, setPlatonPaymentRef] =
			useRefWithSetter<PlatonPayment.Ref | null>(null);
		const [monobankPaymentRef, setMonobankPaymentRef] =
			useRefWithSetter<MonobankPayment.Ref | null>(null);

		const [paymentSystemRef, setPaymentSystemFeeRef] =
			useRefWithSetter<SelectPaymentSystems.Ref | null>(null);
		const [systemFeeRef, setSystemFeeRef] =
			useRefWithSetter<SystemFee.Ref | null>(null);

		const valueEditor = useObjectEditor(value, onChange);

		const paymentProviderValue = valueEditor.useGetter("paymentProvider");

		const isDefault = valueEditor.get("isDefault");

		const name = valueEditor.useGetter("name");
		const setName = valueEditor.useSetter("name");

		const refundPolicyUrl = valueEditor.useGetter("refundPolicyUrl");
		const setRefundPolicyUrl = valueEditor.useSetter("refundPolicyUrl");

		const taxiServicesData = valueEditor.usePicker(
			["taxiServiceIds", "taxiServices", "companies", "companyIds"],
			"json",
		);

		const updateAllData = valueEditor.useAssigner();

		controller.setContext({
			nameRef,
			refundPolicyRef,
			companyBranchRef,
			paymentProviderValue,
			paymentSystemRef,
			systemFeeRef,
			assetPaymentRef,
			maibPaymentRef,
			dinarPaymentRef,
			bogPaymentRef,
			cybersourcePaymentRef,
			platonPaymentRef,
			monobankPaymentRef,
		});
		return (
			<Root hasPaddings>
				<StyledColumn>
					<StyledColumn gap="25px" w="100%" h="100%">
						<Row sizes="50% 50%" gaps="16px*">
							<Name
								ref={setNameRef}
								name={name}
								setName={setName}
								isDefault={isDefault}
							/>
							<Companies
								ref={setCompanyBranchRef}
								value={taxiServicesData}
								onChange={updateAllData}
							/>
						</Row>
						<StyledRow justify="start" gap="16px">
							<SelectPaymentSystems
								ref={setPaymentSystemFeeRef}
								value={value}
								onChange={onChange}
							/>
							<Withdraw
								ref={setSystemFeeRef}
								value={value}
								onChange={onChange}
								paymentProviderValue={paymentProviderValue}
							/>
						</StyledRow>
						<StyledRow gap="20px" justify="space-between" w="100%">
							<StyledColumn gap="10px" w="100%">
								{paymentProviderValue ===
									PaymentProvider.ASSETPAYMENT && (
									<>
										<AssetPayment
											ref={setAssetPaymentRef}
											value={value}
											onChange={onChange}
										/>
										<SystemFee
											ref={setSystemFeeRef}
											value={value}
											onChange={onChange}
										/>
									</>
								)}

								{paymentProviderValue ===
									PaymentProvider.MAIB && (
									<MaibPayment
										ref={setMaibPaymentRef}
										value={value}
										onChange={onChange}
									/>
								)}

								{paymentProviderValue ===
									PaymentProvider.DINARPAY && (
									<DinarPayment
										ref={setDinarPaymentRef}
										value={value}
										onChange={onChange}
									/>
								)}

								{paymentProviderValue ===
									PaymentProvider.BOG && (
									<BogPayment
										ref={setBogPaymentRef}
										value={value}
										onChange={onChange}
									/>
								)}

								{paymentProviderValue ===
									PaymentProvider.CYBERSOURCE && (
									<CybersourcePayment
										ref={setCybersourcePaymentRef}
										value={value}
										onChange={onChange}
									/>
								)}

								{paymentProviderValue ===
									PaymentProvider.PLATON && (
									<PlatonPayment
										ref={setPlatonPaymentRef}
										value={value}
										onChange={onChange}
									/>
								)}

								{paymentProviderValue ===
									PaymentProvider.MONOBANK && (
									<MonobankPayment
										ref={setMonobankPaymentRef}
										value={value}
										onChange={onChange}
									/>
								)}
							</StyledColumn>
						</StyledRow>
						<RefundPolicy
							ref={setRefundPolicyRef}
							value={refundPolicyUrl}
							onChange={setRefundPolicyUrl}
						/>
					</StyledColumn>
				</StyledColumn>
			</Root>
		);
	},
	InternalController,
);
const Content = memo(ContentBase);

declare namespace Content {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: Value;
		onChange: Dispatch<Value>;
		errors?: FieldErrors<any>;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	interface Value extends PaymentSystem.Model {}
}

export default Content;
